import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, BellRing } from "lucide-react";

const CATEGORY_LABEL = {
  next_visit: "Reminder Kunjungan",
  birthday: "Birthday",
  deposit_remind: "Deposit Reminder",
  deposit_upsell: "Deposit Upsell",
  custom: "Custom",
};

const STATUS_COLORS = {
  queued: "bg-amber-100 text-amber-800",
  mock_sent: "bg-indigo-100 text-indigo-800",
  sent: "bg-emerald-100 text-emerald-800",
  failed: "bg-rose-100 text-rose-800",
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const fetchItems = () => {
    setLoading(true);
    const params = filter ? { status: filter } : {};
    api
      .get("/notifications", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const sendOne = async (id) => {
    try {
      const { data } = await api.post(`/notifications/${id}/send`);
      toast.success(`Notifikasi: ${data.status === "sent" ? "Terkirim via Resend" : "MOCK terkirim (Resend belum aktif)"}`);
      fetchItems();
    } catch {
      toast.error("Gagal mengirim notifikasi");
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Notifikasi Outbox</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Reminder kunjungan, birthday, dan deposit follow-up. Status MOCK menandakan
            Resend belum aktif — aktifkan dengan menambahkan <code>RESEND_API_KEY</code> di backend env.
          </p>
        </div>
        <div className="flex gap-2">
          {["", "queued", "mock_sent", "sent"].map((s) => (
            <button
              key={s || "all"}
              data-testid={`filter-${s || "all"}`}
              onClick={() => setFilter(s)}
              className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                filter === s ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"
              }`}
            >
              {s ? s : "Semua"}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <BellRing className="h-4 w-4" /> {items.length} notifikasi
          </CardTitle>
          <CardDescription>Otomatis dibuat dari sistem booking & loyalty.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pasien</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((n) => (
                <TableRow key={n.id} data-testid={`notif-row-${n.id}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{n.patient_name}</div>
                    <div className="text-xs text-muted-foreground">{n.to_email || n.to_phone}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {n.channel === "email" ? (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3 w-3" /> Email
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" /> WA
                        </span>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {CATEGORY_LABEL[n.category] || n.category}
                  </TableCell>
                  <TableCell className="text-sm max-w-[260px] truncate">{n.subject}</TableCell>
                  <TableCell className="text-xs">{fmtDateTime(n.scheduled_at)}</TableCell>
                  <TableCell>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        STATUS_COLORS[n.status] || "bg-secondary"
                      }`}
                    >
                      {n.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {n.status === "queued" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`send-notif-${n.id}`}
                        onClick={() => sendOne(n.id)}
                      >
                        <Send className="h-3.5 w-3.5 mr-1.5" /> Kirim
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                    Tidak ada notifikasi.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
